import { Lock, MessagesSquare, Shield } from 'lucide-react';
import HealoLogo from '@/components/healo-logo';
import LoginPanel from '@/components/layout/login-panel';
import { DashboardContent } from '@/components/dashboard/dashboard-content';
import { useWorkspaceOwnerId } from '@/lib/workspaces/workspace-owner-context';

// This page renders in TWO structural contexts (workspaces-host): as the lock screen
// OUTSIDE any workspace when nobody is signed in, and as a keep-alive PANE inside every
// workspace. The owner context — not auth state — is the discriminator: the lock-screen
// instance has no owner and must show the login UI even while a login is mid-flight, and
// a frozen pane keeps its owner regardless of who is active.
function DashboardPage() {
  const ownerUserId = useWorkspaceOwnerId();
  if (ownerUserId) {
    return <DashboardContent ownerUserId={ownerUserId} />;
  }
  return (
    <div className="relative flex min-h-full flex-1 flex-col overflow-hidden">
      {/* Ambient background */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,var(--color-primary)/0.18,transparent_55%),radial-gradient(ellipse_90%_60%_at_100%_50%,var(--color-primary)/0.08,transparent_50%),radial-gradient(ellipse_80%_50%_at_0%_80%,var(--color-accent)/0.35,transparent_45%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-size-[2.5rem_2.5rem] opacity-[0.35] mask-[radial-gradient(ellipse_80%_70%_at_50%_40%,#000_45%,transparent_100%)] dark:opacity-[0.2]"
        aria-hidden
      />

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-md space-y-10">
          {/* Brand */}
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-5">
              <div className="absolute -inset-3 rounded-3xl bg-primary/15 blur-xl" aria-hidden />
              <div className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl bg-linear-to-br from-primary/20 to-primary/5 ring-1 ring-primary/25 shadow-sm">
                <HealoLogo width={56} height={56} className="h-14 w-14 object-contain" />
              </div>
            </div>
            <h1 className="text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-[2rem]">
              Clarimed
            </h1>
            <p className="mt-2 max-w-sm text-pretty text-sm leading-relaxed text-muted-foreground">
              Clinical messaging in one calm workspace. Sign in to pick up where you left off.
            </p>
          </div>

          <LoginPanel presentation="page" className="w-full" />

          {/* Trust cues */}
          <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-muted-foreground">
            <li className="flex items-center gap-2">
              <Shield className="h-4 w-4 shrink-0 text-primary/80" aria-hidden />
              <span>Authenticated session</span>
            </li>
            <li className="flex items-center gap-2">
              <Lock className="h-4 w-4 shrink-0 text-primary/80" aria-hidden />
              <span>Encrypted credentials</span>
            </li>
            <li className="flex items-center gap-2">
              <MessagesSquare className="h-4 w-4 shrink-0 text-primary/80" aria-hidden />
              <span>Secure messaging</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
